/**
 * VAGUE 33B — SEO City × Category landing page.
 *
 * URL : /annuaire/{citySlug}/{categorySlug}
 * Fetches cached SEO content + businesses from the API, renders SEO-friendly
 * markup with proper meta tags and Schema.org LocalBusiness list.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { MapPin, Star, Phone, ArrowRight, House } from "@phosphor-icons/react";

export default function SeoCityCategoryPage() {
  const params = useParams();
  const citySlug = params.citySlug || params.city;
  const categorySlug = params.categorySlug || params.category;
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/public/seo/city/${citySlug}/category/${categorySlug}`)
      .then((r) => setPage(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Page introuvable"))
      .finally(() => setLoading(false));
  }, [citySlug, categorySlug]);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-16" data-testid="seo-city-category-loading">
        <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"/>
        <p className="text-sm text-muted-foreground mt-3">Chargement de la page…</p>
      </div>
    );
  }
  if (error || !page) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-16 text-center" data-testid="seo-city-category-error">
        <h1 className="font-display text-2xl mb-2">Page introuvable</h1>
        <p className="text-sm text-muted-foreground mb-4">{error || "Cette combinaison n'existe pas."}</p>
        <Link to="/annuaire" className="text-primary underline">← Retour à l'annuaire</Link>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": (page.businesses || []).slice(0, 20).map((b, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "LocalBusiness",
        "name": b.name,
        "url": `${baseUrl}/entreprise/${b.slug}`,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": b.city,
          "postalCode": b.postal_code,
          "addressCountry": "FR",
        },
        ...(b.rating_avg ? {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": b.rating_avg,
            "reviewCount": b.review_count || 0,
          }
        } : {}),
      }
    })),
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="seo-city-category-page">
      <Helmet>
        <title>{page.title || `${page.category} à ${page.city}`}</title>
        {page.meta_description && <meta name="description" content={page.meta_description}/>}
        <link rel="canonical" href={`${baseUrl}/annuaire/${citySlug}/${categorySlug}`}/>
        <meta property="og:title" content={page.title || ""}/>
        <meta property="og:description" content={page.meta_description || ""}/>
        <meta property="og:type" content="website"/>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <nav className="text-xs text-muted-foreground mb-4 flex items-center gap-1" aria-label="Fil d'ariane">
        <Link to="/" className="hover:text-foreground inline-flex items-center gap-1"><House size={12}/> Accueil</Link>
        <span>/</span>
        <Link to="/annuaire" className="hover:text-foreground">Annuaire</Link>
        <span>/</span>
        <span className="text-foreground capitalize">{page.city?.toLowerCase()}</span>
        <span>/</span>
        <span className="text-foreground">{page.category}</span>
      </nav>

      {/* Businesses list first — most useful content */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-medium">
            {page.businesses?.length || 0} commerces référencés à {page.city}
          </h2>
        </div>
        {page.businesses?.length ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="seo-businesses-list">
            {page.businesses.map((b) => (
              <li key={b.slug} className="border border-border rounded-sm p-3 bg-card hover:border-primary transition-colors" data-testid={`seo-biz-${b.slug}`}>
                <Link to={`/entreprise/${b.slug}`} className="block">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-medium">{b.name}</div>
                    {b.premium?.tier && b.premium.tier !== "free" && (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">
                        {b.premium.tier}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin size={11}/> {b.postal_code} {b.city}
                  </div>
                  {b.rating_avg > 0 && (
                    <div className="text-xs mt-1 flex items-center gap-1 text-amber-600">
                      <Star size={11} weight="fill"/> {b.rating_avg.toFixed(1)} ({b.review_count} avis)
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            Aucun commerce référencé pour l'instant. <Link to="/inscription" className="text-primary underline">Inscris le tien →</Link>
          </div>
        )}
      </section>

      {/* SEO article content — 1200 mots */}
      <article
        className="prose prose-sm max-w-none prose-headings:font-display prose-headings:font-medium prose-h1:text-3xl prose-h2:text-xl prose-h2:mt-8 prose-h3:text-base"
        dangerouslySetInnerHTML={{ __html: page.html || "" }}
        data-testid="seo-article"
      />

      {/* Related cross-links (future : add sibling pages) */}
      <div className="mt-10 pt-6 border-t border-border">
        <Link to="/annuaire" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
          Voir tout l'annuaire <ArrowRight size={14}/>
        </Link>
      </div>

      {page.fallback && (
        <div className="mt-6 text-[10px] text-muted-foreground italic">
          Cette page est en cours d'enrichissement.
        </div>
      )}
    </div>
  );
}
